import * as React from 'react';
import {
  useInvoiceTravelInformationStore,
} from 'pages/tracking-v2/invoice-details-page/hooks';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';

import {
  Mountains1SVG,
  Mountains2SVG,
  SkyscrapersSVG,
  ContainersSVG,
  TreesGroup1SVG,
  TreesGroup2SVG,
  TruckSVG,
  MapPinSVG,
} from './images';

type Props = React.ComponentProps<'div'>

export const InvoiceTransportProgression = ({
                                              className,
                                              ...props
                                            }: Props) => {
  const { travelInformation } = useInvoiceTravelInformationStore();

  if (!travelInformation) {
    return (
      <Skeleton className={'w-full h-[240px]'} />
    );
  }

  return (
    <div {...props} className={cn('relative flex flex-col justify-end w-full h-[240px] mb-10', className)}>
      <SVGContainer className={'z-[1] left-[20%]'}><Mountains1SVG /></SVGContainer>
      <SVGContainer className={'z-[1] right-[20%]'}><Mountains2SVG /></SVGContainer>
      <SVGContainer className={'z-[2] left-1/2 -translate-x-1/2'}><SkyscrapersSVG /></SVGContainer>
      <SVGContainer className={'z-[3] left-0'}><ContainersSVG /></SVGContainer>
      <SVGContainer className={'z-[3] left-[15%]'}><TreesGroup1SVG /></SVGContainer>
      <SVGContainer className={'z-[3] left-[75%]'}><TreesGroup2SVG /></SVGContainer>
      <SVGContainer className={'z-[3] right-0 bottom-1'}><MapPinSVG /></SVGContainer>
      <TruckIcon />
      <div className={'relative z-10 w-full h-1 bg-slate-800'} />
      <div className={'absolute left-0 -bottom-6 flex items-center w-full h-[2px] bg-gray-400'}>
        <PositionMarker className={'left-0'} />
        <PositionLabel className={'left-0'}>0 KM</PositionLabel>
        <TruckPositionMarker />
        <TruckPositionLabel />
        <PositionMarker className={'right-0'} />
        <PositionLabel className={'right-0 text-right'}>
          {(travelInformation.totalDistance / 1000).toFixed(2)} KM
        </PositionLabel>
        <RemainingTravelDistanceLine />
      </div>
    </div>
  );
};

const SVGContainer = (props: React.ComponentProps<'figure'>) => (
  <figure {...props} className={cn('absolute bottom-0', props.className)} />
);

const TruckIcon = () => {
  const { travelInformation } = useInvoiceTravelInformationStore();
  const position = travelInformation?.traveledPercentage;

  if (position === undefined) return null;

  const leftPosition =
    position > 1 && ((window.innerWidth / 100) * position - 123) > 0
      ? `calc(${position}% - 123px)`
      : '0';

  return (
    <SVGContainer className={'w-[123px] animate-slide-to-right z-[4]'} style={{
      left: leftPosition,
    }}>
      <TruckSVG />
    </SVGContainer>
  );
};

const TruckPositionMarker = () => {
  const { travelInformation } = useInvoiceTravelInformationStore();
  const position = travelInformation?.traveledPercentage;

  if (position === undefined) return null;

  const hidden = position < 1 || position > 97;
  const leftPosition =
    position > 1 && ((window.innerWidth / 100) * position - 62) > 0
      ? `calc(${position}% - 62px)`
      : '62px';

  return (
    <PositionMarker className={cn(hidden && 'hidden')} style={{ left: leftPosition }} />
  );
};

const TruckPositionLabel = () => {
  const { travelInformation } = useInvoiceTravelInformationStore();
  const position = travelInformation?.traveledPercentage;

  const traveledDistance = travelInformation?.traveledDistance;

  if (position === undefined || traveledDistance === undefined) {
    return null;
  }

  const hidden = position < 1 || position > 97;
  const label = `${traveledDistance / 1000} KM`;
  const leftPosition = position > 1
    ? `calc(${position}% - 82px)`
    : '82px';

  return (
    <PositionLabel
      className={cn('text-right', hidden && 'hidden')}
      style={{ left: leftPosition }}
    >
      {label}
    </PositionLabel>
  );
};

const RemainingTravelDistanceLine = (props: React.ComponentProps<'div'>) => {
  const { travelInformation } = useInvoiceTravelInformationStore();
  const traveledPercentage = travelInformation?.traveledPercentage;
  const remainingDistance = travelInformation?.remainingDistance;

  if (traveledPercentage === undefined) return null;

  const remainingPercentage = 100 - traveledPercentage;
  const hidden = remainingPercentage < 1;
  const width =
    remainingPercentage > 0
      ? `calc(${remainingPercentage}% + 62px)`
      : '100%';

  return (
    <div
      {...props}
      className={cn('absolute flex justify-center max-w-full h-[2px] bg-amber-500 right-0', hidden && 'hidden', props.className)}
      style={{ width }}
    >
      {remainingDistance !== undefined && (
        <PositionLabel className={'bottom-auto -mt-5 font-GilroyBold text-amber-500'}>
          {(remainingDistance / 1000).toFixed(2)} KM
        </PositionLabel>
      )}
    </div>
  );
};

const PositionMarker = (props: React.ComponentProps<'div'>) => (
  <div {...props} className={cn('absolute z-[2] w-2 h-2 bg-gray-400 rounded-full', props.className)} />
);

const PositionLabel = (props: React.ComponentProps<'div'>) => (
  <span
    {...props}
    className={cn('absolute z-[2] -bottom-6 text-[14px] font-GilroySemibold text-slate-600', props.className)}
  />
);


